/**
 * <pip-hero-slider> — one-slide-at-a-time hero carousel.
 *
 * Markup contract (rendered by sections/home-page.liquid):
 *   <pip-hero-slider data-autoplay="true" data-interval="5000">
 *     <div data-hero-track> <div data-hero-slide>…</div> … </div>
 *     <button data-hero-prev></button>
 *     <button data-hero-next></button>
 *     <div data-hero-dots> <button data-hero-dot></button> … </div>
 *   </pip-hero-slider>
 *
 * Slides translate horizontally. Arrows/dots/swipe drive navigation, autoplay
 * is optional and pauses on hover/interaction. Videos play only on the active
 * slide. Respects prefers-reduced-motion and the Shopify theme editor.
 */
class PipHeroSlider extends HTMLElement {
  constructor() {
    super();
    this.track = this.querySelector('[data-hero-track]');
    this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));
    this.prevBtn = this.querySelector('[data-hero-prev]');
    this.nextBtn = this.querySelector('[data-hero-next]');
    this.dots = Array.from(this.querySelectorAll('[data-hero-dot]'));

    this.current = 0;
    this.timer = null;
    this.suppressClick = false;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.autoplay = this.dataset.autoplay === 'true' && !reduceMotion;
    this.interval = parseInt(this.dataset.interval, 10) || 5000;
  }

  connectedCallback() {
    // Single slide: nothing to navigate, just make sure its video runs.
    if (this.slides.length <= 1) {
      this.playActiveVideo(0);
      return;
    }

    this.bindEvents();
    this.goTo(0, false);
    if (this.autoplay) this.startAutoplay();
  }

  disconnectedCallback() {
    this.stopAutoplay();
  }

  bindEvents() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.onUserNav(() => this.prev()));
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.onUserNav(() => this.next()));
    }
    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => this.onUserNav(() => this.goTo(i)));
    });

    // Pause autoplay while the pointer is over the slider.
    this.addEventListener('mouseenter', () => this.stopAutoplay());
    this.addEventListener('mouseleave', () => this.restartAutoplay());

    // Keep the offset correct on resize (translateX is %-based, but this
    // re-syncs in case layout changes the slide width semantics).
    window.addEventListener('resize', () => this.goTo(this.current, false));

    this.setupSwipe();

    // Suppress the link click that follows a drag/swipe (slides are links).
    this.addEventListener(
      'click',
      (e) => {
        if (this.suppressClick) {
          e.preventDefault();
          e.stopPropagation();
          this.suppressClick = false;
        }
      },
      true
    );

    // Theme editor: jump to the slide whose block is selected.
    if (window.Shopify && window.Shopify.designMode) {
      this.addEventListener('shopify:block:select', (e) => {
        const slide = e.target.closest('[data-hero-slide]');
        const index = this.slides.indexOf(slide);
        if (index >= 0) {
          this.stopAutoplay();
          this.goTo(index);
        }
      });
      this.addEventListener('shopify:block:deselect', () => this.restartAutoplay());
    }
  }

  setupSwipe() {
    let startX = 0;
    let endX = 0;
    let dragging = false;
    const threshold = 50;

    const onStart = (x) => {
      startX = x;
      endX = x;
      dragging = true;
      this.stopAutoplay();
    };
    const onMove = (x) => {
      if (dragging) endX = x;
    };
    const onEnd = () => {
      if (!dragging) return;
      dragging = false;
      const diff = endX - startX;
      if (Math.abs(diff) > threshold) {
        this.suppressClick = true;
        diff < 0 ? this.next() : this.prev();
      }
      this.restartAutoplay();
    };

    this.track.addEventListener('touchstart', (e) => onStart(e.touches[0].clientX), { passive: true });
    this.track.addEventListener('touchmove', (e) => onMove(e.touches[0].clientX), { passive: true });
    this.track.addEventListener('touchend', onEnd);

    this.track.addEventListener('mousedown', (e) => onStart(e.clientX));
    this.track.addEventListener('mousemove', (e) => onMove(e.clientX));
    this.track.addEventListener('mouseup', onEnd);
    this.track.addEventListener('mouseleave', onEnd);
  }

  onUserNav(action) {
    this.stopAutoplay();
    action();
    this.restartAutoplay();
  }

  goTo(index, animate = true) {
    const count = this.slides.length;
    this.current = ((index % count) + count) % count;

    if (!animate) this.track.style.transition = 'none';
    this.track.style.transform = `translateX(-${this.current * 100}%)`;
    if (!animate) {
      // Force reflow so the next transform animates again.
      void this.track.offsetHeight;
      this.track.style.transition = '';
    }

    this.dots.forEach((dot, i) => dot.classList.toggle('active', i === this.current));
    this.slides.forEach((slide, i) => slide.setAttribute('aria-hidden', i === this.current ? 'false' : 'true'));
    this.playActiveVideo(this.current);
  }

  next() {
    this.goTo(this.current + 1);
  }

  prev() {
    this.goTo(this.current - 1);
  }

  playActiveVideo(index) {
    this.slides.forEach((slide, i) => {
      const video = slide.querySelector('video');
      if (!video) return;
      if (i === index) {
        const playback = video.play();
        if (playback && playback.catch) playback.catch(() => {});
      } else {
        video.pause();
      }
    });
  }

  startAutoplay() {
    if (!this.autoplay || this.slides.length <= 1) return;
    this.stopAutoplay();
    this.timer = setInterval(() => this.next(), this.interval);
  }

  stopAutoplay() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  restartAutoplay() {
    if (this.autoplay) this.startAutoplay();
  }
}

customElements.define('pip-hero-slider', PipHeroSlider);
